import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, Bug, Lightbulb, HelpCircle, CircleDashed, CheckCircle2, Clock, Filter, Plus, Lock } from "lucide-react";
import { ThreadView, NewFeedbackForm } from "@/pages/partner/PartnerFeedback";

const TYPE_META = {
  bug:        { label: "Bug",        icon: Bug,          tone: "text-rose-300 bg-rose-500/10 border-rose-500/30" },
  suggestion: { label: "Suggestion", icon: Lightbulb,    tone: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  question:   { label: "Question",   icon: HelpCircle,   tone: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30" },
  other:      { label: "Other",      icon: MessageSquare, tone: "text-slate-300 bg-slate-500/10 border-slate-500/30" },
};

const STATUS_META = {
  open:         { label: "Open",         icon: CircleDashed,  tone: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30" },
  in_progress:  { label: "In progress",  icon: Clock,         tone: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  resolved:     { label: "Resolved",     icon: CheckCircle2,  tone: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
};

export default function AdminFeedback() {
  const [threads, setThreads] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterType) params.set("type", filterType);
      const qs = params.toString();
      const { data } = await api.get(`/commissions/partner-feedback${qs ? `?${qs}` : ""}`);
      setThreads(data.threads || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load feedback");
    }
  }, [filterStatus, filterType]);
  useEffect(() => { reload(); }, [reload]);

  const openThread = useMemo(
    () => threads?.find((t) => t.id === openId) || null,
    [threads, openId]
  );

  async function setStatus(id, next) {
    setBusy(true);
    try {
      await api.patch(`/commissions/partner-feedback/${id}/status`, { status: next });
      toast.success(`Marked ${STATUS_META[next]?.label || next}`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  }

  if (openThread) {
    return (
      <div className="space-y-6">
        <ThreadView
          thread={openThread}
          onBack={() => { setOpenId(null); reload(); }}
          role="admin"
          onReplied={reload}
          busy={busy}
          setBusy={setBusy}
        />
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-6" data-testid="admin-feedback-status-controls">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Status</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(STATUS_META).map(([k, m]) => {
              const Icon = m.icon;
              const active = openThread.status === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setStatus(openThread.id, k)}
                  disabled={busy || active}
                  data-testid={`admin-feedback-set-${k}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold uppercase tracking-wider transition-colors ${
                    active ? m.tone + " ring-2 ring-cyan-500/30" : "border-slate-700 bg-slate-950/40 text-slate-400 hover:text-slate-200"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {m.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const unreadCount = threads?.filter((t) => t.is_unread_admin).length || 0;

  return (
    <div className="space-y-8" data-testid="admin-feedback-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl tracking-tight font-medium">Feedback</h1>
          <p className="text-slate-400 mt-1.5 text-sm max-w-2xl">
            Partners submit bugs, suggestions, and questions from their portal — everything lands here.
            Staff can also drop internal notes (marked with a lock) that partners never see.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <div
              data-testid="admin-feedback-unread-badge"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 text-sm font-semibold"
            >
              <MessageSquare className="w-4 h-4" />
              {unreadCount} unread
            </div>
          )}
          {!creating && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              data-testid="admin-feedback-new-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm transition-colors"
            >
              <Plus className="w-4 h-4" /> New internal note
            </button>
          )}
        </div>
      </header>

      {creating && (
        <NewFeedbackForm
          role="admin"
          onCancel={() => setCreating(false)}
          onCreated={(doc) => { setCreating(false); reload(); setOpenId(doc.id); }}
        />
      )}

      <div className="flex items-center gap-3 flex-wrap" data-testid="admin-feedback-filters">
        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500 font-semibold">
          <Filter className="w-3.5 h-3.5" /> Filter
        </span>
        <select
          data-testid="admin-feedback-filter-status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100"
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="resolved">Resolved</option>
        </select>
        <select
          data-testid="admin-feedback-filter-type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100"
        >
          <option value="">All types</option>
          <option value="bug">Bug</option>
          <option value="suggestion">Suggestion</option>
          <option value="question">Question</option>
          <option value="other">Other</option>
        </select>
      </div>

      {threads === null ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : threads.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-10 text-center" data-testid="admin-feedback-empty">
          <MessageSquare className="w-8 h-8 mx-auto text-slate-600 mb-3" />
          <div className="text-slate-300 font-medium">Nothing to show</div>
          <div className="text-slate-500 text-sm mt-1">
            When a partner submits feedback it&apos;ll appear here.
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-feedback-list">
          {threads.map((t) => (
            <AdminThreadRow key={t.id} thread={t} onOpen={() => setOpenId(t.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function AdminThreadRow({ thread, onOpen }) {
  const t = TYPE_META[thread.type] || TYPE_META.other;
  const s = STATUS_META[thread.status] || STATUS_META.open;
  const TIcon = t.icon; const SIcon = s.icon;
  const last = thread.messages?.[thread.messages.length - 1];
  const unread = !!thread.is_unread_admin;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`admin-feedback-row-${thread.id}`}
      className={`w-full text-left rounded-xl border p-5 transition-colors ${
        unread
          ? "border-cyan-500/40 bg-cyan-500/5 hover:bg-cyan-500/10"
          : "border-slate-800 bg-slate-900/60 hover:bg-slate-900"
      }`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${t.tone}`}>
          <TIcon className="w-3 h-3" /> {t.label}
        </div>
        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${s.tone}`}>
          <SIcon className="w-3 h-3" /> {s.label}
        </div>
        {thread.visibility === "admin_only" && (
          <div
            data-testid={`admin-feedback-internal-pill-${thread.id}`}
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10px] font-semibold uppercase tracking-wider"
            title="Internal — partners cannot see this thread"
          >
            <Lock className="w-3 h-3" /> Internal
          </div>
        )}
        {thread.partner_code && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-slate-700 bg-slate-800 text-slate-300 text-[10px] font-mono">
            {thread.partner_code}
          </span>
        )}
        {unread && (
          <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full bg-cyan-500 text-slate-950 text-[10px] font-bold uppercase tracking-wider">
            {thread.visibility === "admin_only" ? "New" : "New from partner"}
          </span>
        )}
      </div>
      <div className="mt-3 font-semibold text-slate-50 truncate">{thread.subject}</div>
      {last && (
        <div className="mt-1.5 text-sm text-slate-400 line-clamp-2 whitespace-pre-wrap">
          <span className="text-slate-500 mr-1">
            {last.author_role === "admin" ? "You:" : `${thread.created_by_name || thread.created_by_email}:`}
          </span>
          {last.body}
        </div>
      )}
      <div className="mt-3 text-[11px] text-slate-500 font-mono">
        {new Date(thread.last_activity_at).toLocaleString()}
        {thread.messages?.length > 1 && ` · ${thread.messages.length} messages`}
      </div>
    </button>
  );
}
